import React from 'react';
import { TableCell, TableRow, IconButton, Tooltip, Chip, Box, Switch, Link } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Campaign } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { CopyButton } from './common';

interface CampaignListItemProps {
  campaign: Campaign;
  onViewDetails: () => void;
  onDelete: (campaign: Campaign) => void;
  onToggleStatus: (campaignId: string, targetStatus: 'enabled' | 'disabled') => Promise<void>;
}

const CampaignListItem: React.FC<CampaignListItemProps> = ({
  campaign,
  onViewDetails,
  onDelete,
  onToggleStatus,
}) => {
  const { canEditCampaigns, canDeleteCampaigns } = useAuth();

  const handleToggleStatus = () => {
    if (campaign.status === 'enabled' || campaign.status === 'disabled') {
      const targetStatus = campaign.status === 'enabled' ? 'disabled' : 'enabled';
      onToggleStatus(campaign.campaign_id, targetStatus);
    }
  };

  const formatDate = (date: string | undefined) => {
    return date ? new Date(date).toLocaleDateString() : '-';
  };

  const getStatusChipColor = () => {
    switch (campaign.status) {
      case 'enabled':
        return 'success';
      case 'disabled':
        return 'default';
      default:
        return 'warning';
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Link
          component="button"
          variant="body2"
          onClick={onViewDetails}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {campaign.name || 'Unnamed Campaign'}
        </Link>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link
            component="button"
            variant="body2"
            onClick={onViewDetails}
            sx={{
              fontFamily: 'monospace',
              cursor: 'pointer',
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {campaign.campaign_id}
          </Link>
          <CopyButton text={campaign.campaign_id} tooltip="Copy Campaign ID" />
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={
              campaign.status
                ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)
                : 'Unknown'
            }
            color={
              getStatusChipColor() as
                | 'default'
                | 'primary'
                | 'secondary'
                | 'error'
                | 'info'
                | 'success'
                | 'warning'
            }
            size="small"
          />
          {canEditCampaigns() &&
            (campaign.status === 'enabled' || campaign.status === 'disabled') && (
              <Switch
                checked={campaign.status === 'enabled'}
                onChange={handleToggleStatus}
                size="small"
              />
            )}
        </Box>
      </TableCell>
      <TableCell>{formatDate(campaign.start_date)}</TableCell>
      <TableCell>{formatDate(campaign.end_date)}</TableCell>
      <TableCell>
        <Tooltip title="View Details">
          <IconButton onClick={onViewDetails} size="small" color="info">
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canDeleteCampaigns() && (
          <Tooltip title="Delete Campaign">
            <IconButton onClick={() => onDelete(campaign)} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

export default CampaignListItem;
